import {
    type ConfirmPostSessionStepResume,
    POST_SESSION_PROCESSING_STEP_ID,
    type PostSessionWorkflowInput,
    type PostSessionWorkflowOutput,
    type RichText,
} from '@fruitful/types';
import { type WorkflowState } from '@mastra/core/workflows';
import { useAtomValue } from 'jotai';
import { useRef, useState } from 'react';
import type { TextStyle } from 'react-native';
import {
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CheckCircleIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    CircleAlertIcon,
    H2Emphasized,
    H6Emphasized,
    Icon,
    IconButton,
    MultilineInput,
    ScrollView,
    TrashIcon,
    useToastController,
    View,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';

import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import {
    appendRichText,
    markdownToDelta,
    RichTextEditor,
    unpackRichText,
} from '../../common/components/RichTextEditor';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { IS_PRODUCTION } from '../../config/FruitfulEnvironment';
import { adminUserAtom } from '../../entities/entityAtoms';
import { useUpsertMembershipGuidanceNotesAndRefetch } from '../../entities/membership/hooks/useUpsertMembershipGuidanceNotes';
import { useGetMembershipDetails } from '../../entities/membership/MembershipDetailsEntityProvider';
import {
    type WorkflowGoalEphemeralId,
    WorkflowGoalsTable,
} from '../../goals/components/WorkflowGoalsTable';
import { useCreateGoalsFromWorkflowGoals } from '../../goals/hooks/useCreateGoalsFromWorkflowGoals';
import { useUpdateGuidanceSession } from '../../guidance-sessions/hooks/useUpdateGuidanceSession';
import { useSendMessage } from '../../guide-messaging/hooks/useSendMessage';
import { TrackedEvent } from '../../logger/Events';
import { captureException, trackEvent } from '../../logger/Logger';
import { useConfirmPostSessionWorkflowOutput } from '../hooks/useConfirmPostSessionWorkflowOutput';
import {
    getConfirmStepResumePayload,
    getPostSessionWorkflowRunTitle,
    getSessionDateFromWorkflowRunState,
    normalizeWorkflowRunStatus,
    WorkflowStatus,
} from '../utils/postSessionWorkflowUtils';

const NOTES_SEPARATOR = '\n\n-----------------------\n\n';

interface AcceptRejectButtonsProps {
    onPressAccept: () => Promise<void>;
    onPressReject: () => Promise<void>;
    disabled: boolean;
    acceptButtonText?: string;
    rejectButtonText?: string;
}

const AcceptRejectButtons = ({
    onPressAccept,
    onPressReject,
    disabled,
    acceptButtonText,
    rejectButtonText,
}: AcceptRejectButtonsProps) => {
    return (
        <XStack justifyContent="flex-start" gap={16} marginTop={16}>
            <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                tone="positive"
                text={acceptButtonText ?? 'Accept'}
                disabled={disabled}
                width="auto"
                style={{ fontWeight: 600 }}
                onPress={() => {
                    void onPressAccept();
                }}
            />
            <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.LARGE}
                text={rejectButtonText ?? 'Delete'}
                disabled={disabled}
                width="auto"
                backgroundColor="$gray400"
                onPress={() => {
                    void onPressReject();
                }}
                icon={TrashIcon}
            />
        </XStack>
    );
};

interface WorkflowRunOutputModalProps {
    isOpen: boolean;
    workflowRun: WorkflowState;
    onClose: () => void;
    refetchWorkflowRuns: () => Promise<void>;
}

const PostSessionWorkflowRunOutputModal = ({
    isOpen,
    onClose,
    workflowRun,
    refetchWorkflowRuns,
}: WorkflowRunOutputModalProps) => {
    const toast = useToastController();
    const adminUser = useAtomValue(adminUserAtom);

    const canView = adminUser.authorization.selectedWorkflowRun.canView;
    const canEdit =
        adminUser.authorization.selectedWorkflowRun.canEdit || !IS_PRODUCTION;
    const canMessage = adminUser.authorization.guideMessaging.canUse;
    const normalizedStatus = normalizeWorkflowRunStatus(workflowRun.status);
    const isWorkflowCompleted = normalizedStatus === WorkflowStatus.SUCCESS;
    const { membershipId, sessionId } =
        workflowRun.payload as PostSessionWorkflowInput;

    const [resumePayload, setResumePayload] =
        useState<ConfirmPostSessionStepResume>(() =>
            getConfirmStepResumePayload(workflowRun),
        );

    const isSummaryConfirmed = resumePayload.confirmSummary != null;
    const isMembershipNotesConfirmed =
        resumePayload.confirmMembershipNotes != null;
    const isGoalsConfirmed = resumePayload.confirmGoals != null;
    const isMessageConfirmed = resumePayload.confirmMessage != null;

    const canActionSummaryOutput = canEdit && !isSummaryConfirmed;
    const canActionMembershipNotesOutput =
        canEdit && !isMembershipNotesConfirmed;
    const canActionGoalsOutput = canEdit && !isGoalsConfirmed;
    const canActionMessageOutput = canEdit && canMessage && !isMessageConfirmed;

    const processingStep = workflowRun.steps?.[POST_SESSION_PROCESSING_STEP_ID];
    const workflowOutput =
        processingStep != null && !Array.isArray(processingStep)
            ? (processingStep.output as PostSessionWorkflowOutput | undefined)
            : undefined;

    const {
        sessionSummary = '',
        membershipNotes = '',
        followUpMessage = '',
        goals,
    } = workflowOutput ?? {};

    const { data: membershipDetails } = useGetMembershipDetails({
        membershipId,
    });

    // Store initial values to detect changes
    // RichTextEditor handles strings and converts them to delta format internally
    const initialSessionSummaryText: RichText = sessionSummary ?? '';
    const initialMembershipNotesText: RichText = membershipNotes ?? '';
    const initialFollowUpMessageText = followUpMessage;
    const initialGoals = (goals as WorkflowGoalEphemeralId[]) ?? [];

    const [sessionSummaryText, setSessionSummaryText] = useState<RichText>(
        markdownToDelta(initialSessionSummaryText) as RichText,
    );
    const [membershipNotesText, setMembershipNotesText] = useState<RichText>(
        markdownToDelta(initialMembershipNotesText) as RichText,
    );
    const [followUpMessageText, setFollowUpMessageText] =
        useState(followUpMessage);
    const [editableGoals, setEditableGoals] =
        useState<WorkflowGoalEphemeralId[]>(initialGoals);

    // Collapsible section states - default to collapsed if already completed
    const [isSummaryCollapsed, setIsSummaryCollapsed] =
        useState(isSummaryConfirmed);
    const [isMembershipNotesCollapsed, setIsMembershipNotesCollapsed] =
        useState(isMembershipNotesConfirmed);
    const [isGoalsCollapsed, setIsGoalsCollapsed] = useState(isGoalsConfirmed);
    const [isMessageCollapsed, setIsMessageCollapsed] =
        useState(isMessageConfirmed);

    const { confirm } = useConfirmPostSessionWorkflowOutput(
        workflowRun.runId,
        workflowRun.resourceId,
    );
    const [isConfirming, setIsConfirming] = useState(false);
    const confirmInFlightRef = useRef(false);
    const { mutateAsync: updateGuidanceSession } = useUpdateGuidanceSession();
    const { create: createGoals } = useCreateGoalsFromWorkflowGoals();
    const { sendMessage } = useSendMessage(membershipDetails);
    const { upsertMembershipGuidanceNotesAsync } =
        useUpsertMembershipGuidanceNotesAndRefetch(membershipDetails ?? null);

    if (!canView) return null;

    const hasRichTextChanged = (
        initial: RichText,
        current: RichText,
    ): boolean => {
        return JSON.stringify(initial) !== JSON.stringify(current);
    };

    const hasGoalsChanged = (
        initial: WorkflowGoalEphemeralId[],
        current: WorkflowGoalEphemeralId[],
    ): boolean => {
        return JSON.stringify(initial) !== JSON.stringify(current);
    };

    const trackWorkflowEvent = (confirmation: ConfirmPostSessionStepResume) => {
        if (membershipDetails == null) return;

        let sectionUpdated = '';
        let sectionHasChanges = false;

        if (confirmation.confirmSummary != null) {
            sectionUpdated = 'summary';
            sectionHasChanges = hasRichTextChanged(
                markdownToDelta(initialSessionSummaryText) as RichText,
                sessionSummaryText,
            );
        } else if (confirmation.confirmMembershipNotes != null) {
            sectionUpdated = 'membershipNotes';
            sectionHasChanges = hasRichTextChanged(
                markdownToDelta(initialMembershipNotesText) as RichText,
                membershipNotesText,
            );
        } else if (confirmation.confirmGoals != null) {
            sectionUpdated = 'goals';
            sectionHasChanges = hasGoalsChanged(initialGoals, editableGoals);
        } else if (confirmation.confirmMessage != null) {
            sectionUpdated = 'message';
            sectionHasChanges =
                initialFollowUpMessageText !== followUpMessageText;
        }

        trackEvent(
            TrackedEvent.WorkflowItem,
            {
                guideId: membershipDetails.guideId ?? '',
                sectionUpdated,
                sectionHasChanges: sectionHasChanges.toString(),
                membershipId,
                workflowId: workflowRun.runId,
            },
            true,
        );
    };

    const onConfirm = async (confirmation: ConfirmPostSessionStepResume) => {
        if (membershipDetails == null) {
            return;
        }

        if (confirmInFlightRef.current) {
            return;
        }

        confirmInFlightRef.current = true;
        setIsConfirming(true);

        try {
            await confirm({
                // Include already confirmed attributes
                ...resumePayload,
                ...confirmation,
            });

            setResumePayload((prev) => ({ ...prev, ...confirmation }));

            // Only execute actions for newly confirmed items that haven't been confirmed before
            if (confirmation.confirmSummary && canActionSummaryOutput) {
                const existingSession = membershipDetails.guidanceSession?.find(
                    (session) => session.id === sessionId,
                );
                const existingSessionNotes = unpackRichText(
                    existingSession?.notes ?? null,
                );

                const appendedNotes = appendRichText(
                    existingSessionNotes,
                    sessionSummaryText,
                    NOTES_SEPARATOR,
                );

                await updateGuidanceSession({
                    urlParams: { id: sessionId },
                    body: { notes: JSON.stringify(appendedNotes) },
                });
            }

            if (
                confirmation.confirmMembershipNotes &&
                canActionMembershipNotesOutput
            ) {
                const existingMembershipNotes = unpackRichText(
                    membershipDetails.membershipGuidanceNotes?.notes ?? null,
                );

                const appendedNotes = appendRichText(
                    existingMembershipNotes,
                    membershipNotesText,
                    NOTES_SEPARATOR,
                );

                await upsertMembershipGuidanceNotesAsync({
                    membershipId,
                    notes: JSON.stringify(appendedNotes),
                });
            }

            if (
                confirmation.confirmGoals &&
                canActionGoalsOutput &&
                editableGoals != null &&
                editableGoals.length > 0
            ) {
                const formattedGoals = editableGoals.map(({ id, ...goal }) => {
                    const formattedTasks = goal?.tasks?.map(
                        ({ id, ...task }) => task,
                    );
                    return {
                        ...goal,
                        tasks: formattedTasks,
                    };
                });
                await createGoals(membershipDetails, formattedGoals);
            }

            if (confirmation.confirmMessage && canActionMessageOutput) {
                await sendMessage(followUpMessageText);
            }

            trackWorkflowEvent(confirmation);

            toast.show('Updated!', MutationSuccessToastOptions);
        } catch (e) {
            toast.show('Something went wrong!', MutationErrorToastOptions(e));
            captureException(e);
        } finally {
            confirmInFlightRef.current = false;
            setIsConfirming(false);
        }

        await refetchWorkflowRuns();
    };

    return (
        <GroveSimpleModal
            isOpen={isOpen}
            onClose={onClose}
            withPadding={false}
            borderRadius={24}
        >
            <ScrollView maxHeight="90vh">
                <YStack width={1200} margin={80} gap={32}>
                    <XStack
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                    >
                        <YStack>
                            <H2Emphasized>
                                Session with{' '}
                                {getPostSessionWorkflowRunTitle(
                                    workflowRun.payload as PostSessionWorkflowInput,
                                )}
                            </H2Emphasized>
                            <BodySmallRegular color="$gray1000">
                                {getSessionDateFromWorkflowRunState(
                                    workflowRun.payload as PostSessionWorkflowInput,
                                )}
                            </BodySmallRegular>
                        </YStack>

                        <IconButton
                            variant="secondary"
                            icon={XIcon}
                            onPress={onClose}
                        />
                    </XStack>

                    <View
                        borderRadius={24}
                        padding={24}
                        backgroundColor="$gray200"
                    >
                        <YStack gap={16}>
                            <XStack
                                alignItems="center"
                                justifyContent="space-between"
                                hoverStyle={{ cursor: 'pointer' }}
                                onPress={() => {
                                    setIsSummaryCollapsed(!isSummaryCollapsed);
                                }}
                            >
                                <XStack alignItems="center" gap={8}>
                                    <Icon
                                        icon={
                                            isSummaryConfirmed
                                                ? resumePayload.confirmSummary ===
                                                  true
                                                    ? CheckCircleIcon
                                                    : XIcon
                                                : CircleAlertIcon
                                        }
                                        color={
                                            isSummaryConfirmed
                                                ? resumePayload.confirmSummary ===
                                                  true
                                                    ? '$green1000'
                                                    : '$red1000'
                                                : '$orange1000'
                                        }
                                        size={16}
                                    />
                                    <H6Emphasized>Session Summary</H6Emphasized>
                                </XStack>
                                <Icon
                                    color="$gray1000"
                                    size={16}
                                    icon={
                                        isSummaryCollapsed
                                            ? ChevronDownIcon
                                            : ChevronUpIcon
                                    }
                                />
                            </XStack>

                            {!isSummaryCollapsed && (
                                <>
                                    <RichTextEditor
                                        placeholder="Session Summary"
                                        initial={sessionSummaryText}
                                        onChange={setSessionSummaryText}
                                        disabled={!canActionSummaryOutput}
                                        containerStyle={{
                                            flexGrow: 1,
                                            flex: 1,
                                            minWidth: 0,
                                            backgroundColor: 'white',
                                        }}
                                        // Approx. 24 lines
                                        height={24 * 24}
                                    />

                                    {!isWorkflowCompleted && (
                                        <AcceptRejectButtons
                                            acceptButtonText="Save session notes"
                                            onPressAccept={async () => {
                                                await onConfirm({
                                                    confirmSummary: true,
                                                });
                                            }}
                                            onPressReject={async () => {
                                                await onConfirm({
                                                    confirmSummary: false,
                                                });
                                            }}
                                            disabled={
                                                !canActionSummaryOutput ||
                                                isConfirming
                                            }
                                        />
                                    )}
                                </>
                            )}
                        </YStack>
                    </View>

                    <View
                        borderRadius={24}
                        padding={24}
                        backgroundColor="$gray200"
                    >
                        <YStack gap={16}>
                            <XStack
                                alignItems="center"
                                justifyContent="space-between"
                                hoverStyle={{ cursor: 'pointer' }}
                                onPress={() => {
                                    setIsMembershipNotesCollapsed(
                                        !isMembershipNotesCollapsed,
                                    );
                                }}
                            >
                                <XStack alignItems="center" gap={8}>
                                    <Icon
                                        icon={
                                            isMembershipNotesConfirmed
                                                ? resumePayload.confirmMembershipNotes ===
                                                  true
                                                    ? CheckCircleIcon
                                                    : XIcon
                                                : CircleAlertIcon
                                        }
                                        color={
                                            isMembershipNotesConfirmed
                                                ? resumePayload.confirmMembershipNotes ===
                                                  true
                                                    ? '$green1000'
                                                    : '$red1000'
                                                : '$orange1000'
                                        }
                                        size={16}
                                    />
                                    <H6Emphasized>
                                        Membership Notes
                                    </H6Emphasized>
                                </XStack>
                                <Icon
                                    color="$gray1000"
                                    size={16}
                                    icon={
                                        isMembershipNotesCollapsed
                                            ? ChevronDownIcon
                                            : ChevronUpIcon
                                    }
                                />
                            </XStack>

                            {!isMembershipNotesCollapsed && (
                                <>
                                    <RichTextEditor
                                        placeholder="Membership notes"
                                        initial={membershipNotesText}
                                        onChange={setMembershipNotesText}
                                        disabled={
                                            !canActionMembershipNotesOutput
                                        }
                                        containerStyle={{
                                            flexGrow: 1,
                                            flex: 1,
                                            minWidth: 0,
                                            backgroundColor: 'white',
                                        }}
                                        height={24 * 12}
                                    />

                                    {!isWorkflowCompleted && (
                                        <AcceptRejectButtons
                                            acceptButtonText="Save membership notes"
                                            onPressAccept={async () => {
                                                await onConfirm({
                                                    confirmMembershipNotes: true,
                                                });
                                            }}
                                            onPressReject={async () => {
                                                await onConfirm({
                                                    confirmMembershipNotes: false,
                                                });
                                            }}
                                            disabled={
                                                !canActionMembershipNotesOutput ||
                                                isConfirming
                                            }
                                        />
                                    )}
                                </>
                            )}
                        </YStack>
                    </View>

                    <View
                        borderRadius={24}
                        padding={24}
                        backgroundColor="$gray200"
                        flex={1}
                    >
                        <YStack flex={1} gap={16}>
                            <XStack
                                alignItems="center"
                                justifyContent="space-between"
                                gap={12}
                                flex={1}
                                hoverStyle={{ cursor: 'pointer' }}
                                onPress={() => {
                                    setIsGoalsCollapsed(!isGoalsCollapsed);
                                }}
                            >
                                <XStack alignItems="center" gap={8}>
                                    <Icon
                                        icon={
                                            isGoalsConfirmed
                                                ? resumePayload.confirmGoals ===
                                                  true
                                                    ? CheckCircleIcon
                                                    : XIcon
                                                : CircleAlertIcon
                                        }
                                        color={
                                            isGoalsConfirmed
                                                ? resumePayload.confirmGoals ===
                                                  true
                                                    ? '$green1000'
                                                    : '$red1000'
                                                : '$orange1000'
                                        }
                                        size={16}
                                    />
                                    <H6Emphasized>Goals</H6Emphasized>
                                </XStack>
                                <Icon
                                    color="$gray1000"
                                    size={16}
                                    icon={
                                        isGoalsCollapsed
                                            ? ChevronDownIcon
                                            : ChevronUpIcon
                                    }
                                />
                                {/* 
                                To be wired-up at a later date

                                <Button
                                    variant={ButtonVariant.TERTIARY}
                                    size={ButtonSize.TINY}
                                    textProps={{ color: '$green1000' }}
                                    text="Add new goal"  
                                    width='auto'
                                    onPress={() => { }}
                                /> */}
                            </XStack>

                            {!isGoalsCollapsed && (
                                <>
                                    {(goals?.length ?? 0) > 0 && (
                                        <WorkflowGoalsTable
                                            membershipId={membershipId}
                                            goals={
                                                goals as WorkflowGoalEphemeralId[]
                                            }
                                            onGoalsChange={(
                                                updated: WorkflowGoalEphemeralId[],
                                            ) => {
                                                setEditableGoals(updated);
                                            }}
                                            disabled={!canActionGoalsOutput}
                                        />
                                    )}

                                    {!isWorkflowCompleted && (
                                        <AcceptRejectButtons
                                            acceptButtonText="Publish goals"
                                            onPressAccept={async () => {
                                                await onConfirm({
                                                    confirmGoals: true,
                                                });
                                            }}
                                            onPressReject={async () => {
                                                await onConfirm({
                                                    confirmGoals: false,
                                                });
                                            }}
                                            disabled={
                                                !canActionGoalsOutput ||
                                                isConfirming
                                            }
                                        />
                                    )}
                                </>
                            )}
                        </YStack>
                    </View>

                    <View
                        borderRadius={24}
                        padding={24}
                        backgroundColor="$gray200"
                        flex={1}
                    >
                        <YStack flex={1} gap={16}>
                            <XStack
                                alignItems="center"
                                justifyContent="space-between"
                                gap={12}
                                hoverStyle={{ cursor: 'pointer' }}
                                onPress={() => {
                                    setIsMessageCollapsed(!isMessageCollapsed);
                                }}
                            >
                                <XStack alignItems="center" gap={8}>
                                    <Icon
                                        icon={
                                            isMessageConfirmed
                                                ? resumePayload.confirmMessage ===
                                                  true
                                                    ? CheckCircleIcon
                                                    : XIcon
                                                : CircleAlertIcon
                                        }
                                        color={
                                            isMessageConfirmed
                                                ? resumePayload.confirmMessage ===
                                                  true
                                                    ? '$green1000'
                                                    : '$red1000'
                                                : '$orange1000'
                                        }
                                        size={16}
                                    />
                                    <H6Emphasized>
                                        Follow up message
                                    </H6Emphasized>
                                </XStack>
                                <Icon
                                    color="$gray1000"
                                    size={16}
                                    icon={
                                        isMessageCollapsed
                                            ? ChevronDownIcon
                                            : ChevronUpIcon
                                    }
                                />

                                {/* {
                                To be wired-up at a later date
                                canMessage && (
                                    <Button
                                        variant={ButtonVariant.TERTIARY}
                                        size={ButtonSize.TINY}
                                        textProps={{ color: '$green1000' }}
                                        text="Open chat"
                                        icon={
                                            <Icon
                                                icon={ArrowUpRightIcon}
                                                color="$green1000"
                                                size={14}
                                            />
                                        }
                                        iconPosition="right"
                                        width="auto"
                                        onPress={() => {
                                            onClose();
                                            router.push('/guide-messaging');
                                        }}
                                    />
                                )} */}
                            </XStack>

                            {!isMessageCollapsed && (
                                <>
                                    <MultilineInput
                                        fontSize={14}
                                        padding={0}
                                        borderRadius="none"
                                        borderWidth={0}
                                        borderColor="transparent"
                                        outlineWidth={0}
                                        hoverStyle={{
                                            borderColor: 'transparent',
                                            borderWidth: 0,
                                            outlineWidth: 0,
                                        }}
                                        focusStyle={{
                                            borderColor: 'transparent',
                                            borderWidth: 0,
                                            outlineWidth: 0,
                                        }}
                                        focusVisibleStyle={{
                                            borderColor: 'transparent',
                                            borderWidth: 0,
                                            outlineWidth: 0,
                                        }}
                                        value={followUpMessageText}
                                        onChangeText={setFollowUpMessageText}
                                        overflow="hidden"
                                        style={
                                            {
                                                fieldSizing: 'content',
                                                minHeight: '200px',
                                                height: 'revert',
                                            } as unknown as TextStyle
                                        }
                                        disabled={!canActionMessageOutput}
                                        containerProps={{ flex: 1 }}
                                    />

                                    {!isWorkflowCompleted && (
                                        <AcceptRejectButtons
                                            acceptButtonText="Send message"
                                            onPressAccept={async () => {
                                                await onConfirm({
                                                    confirmMessage: true,
                                                });
                                            }}
                                            onPressReject={async () => {
                                                await onConfirm({
                                                    confirmMessage: false,
                                                });
                                            }}
                                            disabled={
                                                !canActionMessageOutput ||
                                                isConfirming
                                            }
                                        />
                                    )}
                                </>
                            )}
                        </YStack>
                    </View>
                </YStack>
            </ScrollView>
        </GroveSimpleModal>
    );
};

export default PostSessionWorkflowRunOutputModal;
